import { API_URL, apiFetch as fetch } from '../lib/api';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';


const INTEREST_TYPES = [
  { value: 'pilot', label: 'I want to evaluate the closed pilot' },
  { value: 'researcher', label: 'I am a researcher looking for data' },
  { value: 'institution', label: 'I represent an institution / site' },
  { value: 'patient', label: 'I am a patient with questions' },
  { value: 'partnership', label: 'Partnership or sponsorship' },
  { value: 'other', label: 'Something else' },
];

const Contact = () => {
  const [searchParams] = useSearchParams();
  const requestedInterest = searchParams.get('interest');
  const initialInterest = INTEREST_TYPES.some((item) => item.value === requestedInterest)
    ? requestedInterest
    : 'pilot';
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    interest_type: initialInterest,
    message: '',
  });
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'sending', message: '' });
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      setStatus({ state: 'sent', message: data.message || 'Thanks — we received your message.' });
      setForm({ name: '', email: '', organization: '', interest_type: initialInterest, message: '' });
    } catch (err) {
      setStatus({ state: 'error', message: err.message || 'Could not reach the server. Please try again.' });
    }
  };

  const inputClass =
    'w-full bg-transparent border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-emerald-400 focus:outline-none';

  return (
    <div className="min-h-screen bg-black text-white">
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm text-emerald-400 uppercase tracking-wider mb-4">Contact</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Get in touch</h1>
            <p className="text-white/40 mb-10">
              Tell us what you want to evaluate or help design. We review pilot requests directly.
            </p>
          </motion.div>

          <div role="status" aria-live="polite" className="sr-only">
            {status.state === 'sending' ? 'Sending your message…' : status.state === 'sent' ? `Message received. ${status.message}` : ''}
          </div>
          {status.state === 'sent' ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border border-emerald-400/30 bg-emerald-400/5 p-8"
            >
              <p className="text-emerald-400 text-lg mb-2">Message received</p>
              <p className="text-white/60 text-sm mb-6">{status.message}</p>
              <button
                onClick={() => setStatus({ state: 'idle', message: '' })}
                className="text-sm text-white/40 hover:text-white underline"
              >
                Send another message
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                    Name
                  </label>
                  <input
                    id="contact-name" name="name" autoComplete="name"
                    type="text"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Jane Doe"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                    Email
                  </label>
                  <input
                    id="contact-email" name="email" autoComplete="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update('email')}
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-organization" className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                  Organization
                </label>
                <input
                  id="contact-organization" name="organization" autoComplete="organization"
                  type="text"
                  required
                  maxLength={200}
                  value={form.organization}
                  onChange={update('organization')}
                  placeholder="University, hospital, company, or 'Independent'"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="contact-interest" className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                  What brings you here?
                </label>
                <select id="contact-interest" name="interest_type" value={form.interest_type} onChange={update('interest_type')} className={inputClass}>
                  {INTEREST_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-black">
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                  Message
                </label>
                <textarea
                  id="contact-message" name="message" aria-describedby="contact-privacy"
                  required
                  minLength={10}
                  maxLength={4000}
                  rows={6}
                  value={form.message}
                  onChange={update('message')}
                  placeholder="Describe your study, your data needs, or your question."
                  className={inputClass}
                />
              </div>

              {status.state === 'error' && (
                <p role="alert" className="text-red-400 text-sm">{status.message}</p>
              )}

              <button
                type="submit"
                disabled={status.state === 'sending'}
                className="px-8 py-3 bg-emerald-500 text-black font-medium hover:bg-emerald-400 transition-colors disabled:opacity-50"
              >
                {status.state === 'sending' ? 'Sending…' : 'Send message'}
              </button>

              <p id="contact-privacy" className="text-white/30 text-xs leading-relaxed">
                Please don't include patient identifiers or protected health information in this
                form. It is a general enquiry channel, not a clinical data pathway.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;
